import { useState, useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { Message } from '../types/chat';

const SOCKET_URL2 = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && window.supportlyChatConfig?.socketUrl) || '/';

interface UseChatParams {
  name: string;
  sessionId: string;
}

interface ActiveLimitConsentState {
  hasActiveCase: boolean;
  caseId: string | null;
  status: string | null;
  currentAttempt: number | null;
  nextQuestion: {
    questionKey: string;
    questionText: string;
  } | null;
  requiresProof: boolean;
}

interface ArchivedConversationSummary {
  date: string;
  messageCount: number;
  firstMessageAt: string;
  lastMessageAt: string;
  preview: string;
}

export const useChat = ({ name, sessionId }: UseChatParams) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const limit = 40;
  const originalTitle = useRef(document.title);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const stored = localStorage.getItem('soundEnabled');
    return stored === null ? true : stored === 'true';
  });
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [activeLimitConsent, setActiveLimitConsent] = useState<ActiveLimitConsentState>({
    hasActiveCase: false,
    caseId: null,
    status: null,
    currentAttempt: null,
    nextQuestion: null,
    requiresProof: false,
  });
  const [submittingConsentAnswer, setSubmittingConsentAnswer] = useState(false);
  const [archiveConversations, setArchiveConversations] = useState<ArchivedConversationSummary[]>([]);
  const [activeConversationDate, setActiveConversationDate] = useState<string | null>(null);
  const isChatOpenRef = useRef(false);
  const soundEnabledRef = useRef(soundEnabled);
  const activeConversationDateRef = useRef<string | null>(null);
  const markReadInFlightRef = useRef(false);
  const lastMarkReadAtRef = useRef(0);

  const getAuthHeaders = useCallback(
    () => ({
      'X-Anonymous-User': encodeURIComponent(sessionId),
      'X-Anonymous-Name': encodeURIComponent(name),
    }),
    [name, sessionId]
  );

  const readJsonResponse = useCallback(async (response: Response) => {
    const contentType = response.headers.get('content-type') || '';
    const responseText = await response.text();

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    if (!contentType.includes('application/json')) {
      throw new Error(`Unexpected content type: ${contentType || 'unknown'}`);
    }

    return responseText ? JSON.parse(responseText) : {};
  }, []);

  const fetchActiveLimitConsent = useCallback(async () => {
    try {
      const response = await fetch(`${SOCKET_URL2}/api/limit-consent/active`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        setActiveLimitConsent({
          hasActiveCase: false,
          caseId: null,
          status: null,
          currentAttempt: null,
          nextQuestion: null,
          requiresProof: false,
        });
        return;
      }

      const payload = await readJsonResponse(response);
      setActiveLimitConsent({
        hasActiveCase: Boolean(payload?.hasActiveCase),
        caseId: payload?.caseId || null,
        status: payload?.status || null,
        currentAttempt:
          typeof payload?.currentAttempt === 'number' ? payload.currentAttempt : null,
        nextQuestion: payload?.nextQuestion || null,
        requiresProof: Boolean(payload?.requiresProof),
      });
    } catch {
      setActiveLimitConsent({
        hasActiveCase: false,
        caseId: null,
        status: null,
        currentAttempt: null,
        nextQuestion: null,
        requiresProof: false,
      });
    }
  }, [getAuthHeaders, readJsonResponse]);

  const fetchArchiveConversations = useCallback(async () => {
    try {
      const response = await fetch(`${SOCKET_URL2}/api/messages/archive-sessions?limit=10`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        setArchiveConversations([]);
        return;
      }
      const payload = await readJsonResponse(response);
      setArchiveConversations(Array.isArray(payload?.conversations) ? payload.conversations : []);
    } catch {
      setArchiveConversations([]);
    }
  }, [getAuthHeaders, readJsonResponse]);

  const fetchMessages = useCallback(
    async (pageNum = 1, conversationDate?: string | null) => {
      const query = new URLSearchParams({
        sessionId,
        page: String(pageNum),
        limit: String(limit),
      });
      if (conversationDate) {
        query.set('conversationDate', conversationDate);
      }

      try {
        const res = await fetch(`${SOCKET_URL2}/api/messages?${query.toString()}`, {
          headers: getAuthHeaders(),
        });
        const data = await readJsonResponse(res);
        if (pageNum === 1) {
          setMessages(data.messages || []);
          if (!conversationDate) {
            const unread = (data.messages || []).filter(
              (m: Message) => m.sender !== sessionId && m.status !== 'read'
            ).length;
            setUnreadCount(unread);
          }
        } else {
          setMessages(prev => [...(data.messages || []), ...prev]);
        }
        setHasMore((data.messages || []).length === limit);
      } catch (error) {
        console.warn('[support-chat] Could not load messages.', error);
        if (pageNum === 1) {
          setMessages([]);
        }
        setHasMore(false);
      }
    },
    [getAuthHeaders, limit, readJsonResponse, sessionId]
  );

  useEffect(() => {
    isChatOpenRef.current = isChatOpen;
  }, [isChatOpen]);

  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  useEffect(() => {
    activeConversationDateRef.current = activeConversationDate;
  }, [activeConversationDate]);

  useEffect(() => {
    const userAuth = {
      userId: sessionId,
      name,
      isAnonymous: true,
    };
    const socketInstance = io(SOCKET_URL2, {
      auth: userAuth,
      transports: ['polling', 'websocket'],
      withCredentials: false,
      path: '/socket.io',
    });
    setSocket(socketInstance);

    socketInstance.on('connect', () => {
      socketInstance.emit('join', sessionId);
    });

    socketInstance.on('new_message', (message: Message) => {
      const currentArchiveDate = activeConversationDateRef.current;
      const incomingDate = message.timestamp ? message.timestamp.slice(0, 10) : null;
      if (!currentArchiveDate || incomingDate === currentArchiveDate) {
        setMessages((prev) => [...prev, message]);
      }
      fetchArchiveConversations();
      if (String(message.sender || '') === 'admin') {
        fetchActiveLimitConsent();
      }
      if (!isChatOpenRef.current && message.sender !== sessionId) {
        setUnreadCount((prev) => prev + 1);
      }
      if (audioRef.current && soundEnabledRef.current && message.sender !== sessionId) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});
      }
      if (message.sender !== sessionId && !document.hasFocus()) {
        setHasNewMessage(true);
      }
    });

    socketInstance.on('user_typing', ({ userId }) => {
      setTypingUsers((prev) => (prev.includes(userId) ? prev : [...prev, userId]));
    });

    socketInstance.on('user_stop_typing', ({ userId }) => {
      setTypingUsers((prev) => prev.filter((id) => id !== userId));
    });

    return () => {
      socketInstance.removeAllListeners();
      socketInstance.close();
    };
  }, [sessionId, name, fetchActiveLimitConsent, fetchArchiveConversations]);

  useEffect(() => {
    if (!sessionId) return;
    setActiveConversationDate(null);
    fetchMessages(1, null);
    fetchArchiveConversations();
    fetchActiveLimitConsent();
    setPage(1);
  }, [sessionId, fetchActiveLimitConsent, fetchArchiveConversations, fetchMessages]);
  
    useEffect(() => {
    if (!socket || !sessionId || !name) return;
    const emitOnline = () => {
      socket.emit('user_online', {
        sessionId,
        userId: sessionId,
        name,
        isAnonymous: true
      });
    };
    socket.on('connect', emitOnline);
    window.addEventListener('focus', emitOnline);
    // Emit immediately if already connected
    if (socket.connected) emitOnline();
    return () => {
      socket.off('connect', emitOnline);
      window.removeEventListener('focus', emitOnline);
    };
  }, [socket, sessionId, name]);

  const handleSendMessage = async (content: string, file?: File) => {
    if (activeConversationDate) return;
    if (!socket) return;
    if (file) {
      const message = {
        content,
        senderName: name,
        sender: sessionId,
        timestamp: new Date().toISOString(),
        status: 'sending',
        sessionId,
      };
      const formData = new FormData();
      formData.append('file', file);
      formData.append('message', JSON.stringify(message));
      try {
        const response = await fetch(`${SOCKET_URL2}/api/messages/upload`, {
          method: 'POST',
          body: formData,
          headers: getAuthHeaders(),
        });
        if (!response.ok) {
          throw new Error('File upload failed');
        }
        const data = await readJsonResponse(response);
        socket.emit('send_message', data);
      } catch (err) {
        console.warn('[support-chat] File upload failed.', err);
      }
      return;
    }
    const message = {
      content,
      senderName: name,
      sender: sessionId,
      timestamp: new Date().toISOString(),
      status: 'sending',
      sessionId,
    };
    socket.emit('send_message', message);
  };

  const openArchivedConversation = useCallback(
    (date: string) => {
      setActiveConversationDate(date);
      setPage(1);
      fetchMessages(1, date);
    },
    [fetchMessages]
  );

  const openActiveConversation = useCallback(() => {
    setActiveConversationDate(null);
    setPage(1);
    fetchMessages(1, null);
    fetchArchiveConversations();
    fetchActiveLimitConsent();
  }, [fetchActiveLimitConsent, fetchArchiveConversations, fetchMessages]);

  const submitConsentAnswer = useCallback(
    async (answer: 'yes' | 'no') => {
      if (!activeLimitConsent.caseId || !activeLimitConsent.nextQuestion) {
        return;
      }
      if (submittingConsentAnswer) {
        return;
      }

      setSubmittingConsentAnswer(true);
      try {
        const response = await fetch(
          `${SOCKET_URL2}/api/limit-consent/${activeLimitConsent.caseId}/answer`,
          {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            questionKey: activeLimitConsent.nextQuestion.questionKey,
            answer,
          }),
          }
        );

        if (!response.ok) {
          return;
        }

        await readJsonResponse(response).catch(() => null);
      } finally {
        setSubmittingConsentAnswer(false);
        fetchActiveLimitConsent();
      }
    },
    [
      activeLimitConsent.caseId,
      activeLimitConsent.nextQuestion,
      fetchActiveLimitConsent,
      getAuthHeaders,
      readJsonResponse,
      submittingConsentAnswer,
    ]
  );

  const openChat = useCallback(() => {
    setIsChatOpen(true);
    setUnreadCount(0);

    const now = Date.now();
    const hasRecentMarkRead = now - lastMarkReadAtRef.current < 1500;
    if (!markReadInFlightRef.current && !hasRecentMarkRead) {
      markReadInFlightRef.current = true;
      fetch(`${SOCKET_URL2}/api/messages/mark-read?sessionId=${sessionId}&role=client`, {
        method: 'POST',
        headers: getAuthHeaders(),
      })
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
        })
        .catch((error) => {
          console.warn('[support-chat] Could not mark messages as read.', error);
        })
        .finally(() => {
          markReadInFlightRef.current = false;
          lastMarkReadAtRef.current = Date.now();
        });
    }

    setMessages(msgs =>
      msgs.map((m: Message) =>
        m.sender !== sessionId && m.status !== 'read'
          ? { ...m, status: 'read' }
          : m
      )
    );
    fetchArchiveConversations();
    fetchActiveLimitConsent();
  }, [fetchActiveLimitConsent, fetchArchiveConversations, getAuthHeaders, sessionId]);

  const closeChat = () => {
    setIsChatOpen(false);
  };

  useEffect(() => {
    const onFocus = () => setHasNewMessage(false);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  useEffect(() => {
    if (hasNewMessage) {
      document.title = '(*) ' + originalTitle.current;
    } else {
      document.title = originalTitle.current;
    }
  }, [hasNewMessage]);

  const loadMore = () => {
    if (!hasMore) return;
    const nextPage = page + 1;
    fetchMessages(nextPage, activeConversationDate);
    setPage(nextPage);
  };

  return {
    socket,
    messages,
    typingUsers,
    isChatOpen,
    unreadCount,
    soundEnabled,
    audioRef,
    handleSendMessage,
    openChat,
    closeChat,
    setSoundEnabled,
    loadMore,
    hasMore,
    activeLimitConsent,
    submittingConsentAnswer,
    submitConsentAnswer,
    archiveConversations,
    activeConversationDate,
    openArchivedConversation,
    openActiveConversation,
  };
};
